import * as inslyticSdk from '@inslytic/sdk-browser';

// Mixpanel-only keys: `$insert_id` and `time` drive Mixpanel's cross-tab dedupe, which Inslytic
// does not have, and Inslytic stamps its own timestamp.
const MIXPANEL_ONLY_KEYS = new Set(['$insert_id', 'time']);

let enabled = false;

/**
 * Inslytic accepts string property values only, and rejects the whole batch when one is not a
 * string. null and undefined are dropped, so "no space" reads as a missing spaceId.
 */
export const toInslyticProperties = (payload: Record<string, unknown>): Record<string, string> => {
  const properties: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined || MIXPANEL_ONLY_KEYS.has(key)) continue;
    properties[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
  return properties;
};

/** A second destination for every app event. Off when VITE_INSLYTIC_API_KEY is not set. */
export const inslytic = {
  initialize: () => {
    const apiKey = import.meta.env.VITE_INSLYTIC_API_KEY as string | undefined;
    if (!apiKey) return;
    enabled = true;
    // Page views are tracked by the SDK itself.
    inslyticSdk.init({ apiKey });
    // The app tracks signed-in usage without a consent banner, as it does for Mixpanel.
    inslyticSdk.setConsent(true);
  },

  track: (event: string, payload: Record<string, unknown>) => {
    if (!enabled) return;
    inslyticSdk.track(event, { properties: toInslyticProperties(payload) });
  },

  identify: (userId: string) => {
    if (!enabled) return;
    inslyticSdk.identify(userId);
  },

  logout: () => {
    if (!enabled) return;
    // Clears the identity and stops the SDK. Logout leaves the page, and the next page load
    // initializes it again.
    inslyticSdk.reset();
  },
};
